import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutTemplate, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

export const TemplateLibrary = ({ programId = "" }) => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [using, setUsing] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const use = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/templates/${using.id}/use`, { name, program_id: programId });
      toast.success("Campaign created from template");
      nav(`/admin/campaigns/${data.id}/edit`);
    } catch (ex) { toast.error(formatErr(ex)); }
    finally { setBusy(false); }
  };

  const del = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? Campaigns made from it are not affected.`)) return;
    await api.delete(`/templates/${t.id}`);
    toast.success("Template deleted");
    load();
  };

  if (!templates.length) return null;
  return (
    <div className="mb-12 sv-rise sv-d3" data-testid="template-library">
      <div className="flex items-center gap-2 mb-4">
        <LayoutTemplate className="w-4 h-4 text-indigo-400" />
        <h2 className="font-display font-semibold text-lg">Templates</h2>
        <span className="font-mono text-xs text-slate-500">{templates.length}</span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map((t) => (
          <div key={t.id} data-testid={`template-card-${t.id}`} className="rounded-lg border border-[#242C3D] bg-[#141A24]/70 p-5 hover:border-indigo-500/40 transition-colors">
            <div className="flex items-start justify-between gap-2 mb-1">
              <h3 className="font-semibold text-slate-100 leading-snug">{t.name}</h3>
              {user?.role === "admin" && (
                <button data-testid={`template-delete-${t.id}`} onClick={() => del(t)} className="text-slate-500 hover:text-rose-400 transition-colors" title="Delete template"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
            <p className="text-xs text-slate-500 mb-4">By {t.created_by} · updated {fmtDate(t.updated_at)}</p>
            {using?.id === t.id ? (
              <form onSubmit={use} className="flex gap-2">
                <input data-testid="template-use-name-input" autoFocus required value={name} onChange={(e) => setName(e.target.value)} placeholder="New campaign name"
                  className="flex-1 h-9 px-3 rounded-md bg-[#0B0F17] border border-[#242C3D] text-sm focus:border-indigo-500 focus:outline-none" />
                <button data-testid="template-use-submit-button" disabled={busy} className="h-9 px-3 rounded-md bg-indigo-500 hover:bg-indigo-400 text-sm font-medium disabled:opacity-60">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create"}
                </button>
              </form>
            ) : (
              <button data-testid={`template-use-${t.id}`} onClick={() => { setUsing(t); setName(`${t.name} campaign`); }}
                className="h-9 px-4 rounded-full border border-[#242C3D] hover:bg-white/5 text-sm transition-colors">Use template</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
